// repoBrowse.mjs — 第八十六批：管理员浏览源码仓的纯逻辑。
//
// 需求方裁：「粗略查看『项目源码类 git 仓库』所含文件，文本类文件支持直接展示，
// 所有的二进制只支持下载即可，提交历史可以仅精确到分支」。
//
// 抽成纯模块的理由与 groupCreate.mjs 同：面包屑的切分、目录项的排序、以及
// "这个响应该渲染成什么"这三件事都有边界（仓根、末级、空目录、超限文本），
// 而它们写在 JSX 里就只能被点击测到——于是实际上没人测。

// ── 面包屑 ──────────────────────────────────────────────────────────────────
// "a/b/c.txt" → [{name:'仓根',path:''},{name:'a',path:'a'},{name:'b',path:'a/b'},
//                {name:'c.txt',path:'a/b/c.txt'}]
// 仓根那一节的 path 是空串——它就是"不带 path 参数"的那次请求，与后端
// `path 为空 ⇒ 仓根` 的口径一致。
export function breadcrumb(path, rootLabel = '/') {
  const parts = String(path ?? '').split('/').filter(Boolean);
  const out = [{ name: rootLabel, path: '' }];
  let acc = '';
  for (const p of parts) {
    acc = acc ? `${acc}/${p}` : p;
    out.push({ name: p, path: acc });
  }
  return out;
}

// 上一级的路径。仓根的上一级仍是仓根（返回 null 表示"已经在顶了"，
// 让调用方能把返回键禁用掉而不是让它跳去一个不存在的地方）。
export function parentPath(path) {
  const s = String(path ?? '');
  if (!s) return null;
  const i = s.lastIndexOf('/');
  return i < 0 ? '' : s.slice(0, i);
}

// ── 目录排序 ────────────────────────────────────────────────────────────────
// 目录在前、同类按名字。**大小写不敏感**排序：`README.md` 与 `src/` 混在一起时，
// 按字节序会把所有大写文件排到小写之前，读起来像是随机的。
export function sortEntries(entries) {
  const rank = (e) => (e.type === 'dir' ? 0 : 1);
  return [...(entries ?? [])].sort((a, b) => {
    const r = rank(a) - rank(b);
    if (r !== 0) return r;
    return String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined,
      { sensitivity: 'base', numeric: true });
  });
}

// ── 这个响应该渲染成什么 ────────────────────────────────────────────────────
// 后端已经判好了 kind / inline / reason，前端**不重判一遍**——重判就是把
// "什么算文本"这条规则放到两处，而两处迟早会漂开（本仓已经为这类漂移付过几次）。
// 这个函数只是把后端的判断翻译成一个渲染分支，外加一条前端才知道的事：
// 空目录要有话说，而不是一片空白。
export function viewOf(payload) {
  const p = payload ?? {};
  if (p.kind === 'dir') {
    const entries = sortEntries(p.entries);
    return entries.length === 0
      ? { view: 'empty-dir', entries: [] }
      : { view: 'dir', entries };
  }
  if (p.kind !== 'file') return { view: 'unknown' };
  if (p.inline) return { view: 'text', text: p.text ?? '' };
  // 不直展的两种理由要分开说：「这是二进制」与「这个文本太大了」对用户
  // 是两件事，前者没有别的选择，后者是"你可以下载了自己看"。
  //
  // canDownload 与 path **必须透传**（第一百三十四批实撞）：第一百一十八批后端不再回
  // downloadUrl、改回 canDownload 布尔 + 前端自拼地址（adminRepoRawUrl 要 path），
  // 而这层翻译当时没跟着改——页面读的是**视图**不是原始应答，于是提示语在、
  // 按钮永不渲染：「请下载后打开」配一个不存在的下载口。
  return { view: 'download', reason: p.reason === 'binary' ? 'binary' : 'too-large',
           canDownload: !!p.canDownload, path: p.path ?? '',
           size: p.size ?? 0, limit: p.inlineLimitBytes ?? null };
}

// ── 显示用的字节数 ──────────────────────────────────────────────────────────
export function fmtBytes(n) {
  const v = Number(n);
  if (!Number.isFinite(v) || v < 0) return '—';
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KiB`;
  return `${(v / 1024 / 1024).toFixed(1)} MiB`;
}

// ── 语法高亮的粗略语言名（只用于给 <pre> 打个 class，不做真高亮）─────────────
// 「粗略」是裁决里的词——真高亮要引一个库，而这一页的用途是"看看仓里有什么"。
export function langOf(path) {
  const m = String(path ?? '').toLowerCase().match(/\.([a-z0-9]+)$/);
  return m ? m[1] : '';
}

// ── 第九十批：给协作者用的公网克隆地址 ──────────────────────────────────────
// 需求方在给两位成员发 tech-demo 凭据时想到：浏览页应该能看到**当前选中仓库的
// 公网访问地址与配套凭据的使用方式**。此前这两件事都只存在于发凭据那个人的脑子里
// ——他要自己记得路径前缀是 `/git/`、记得域名随 stage 变、记得凭据是 Basic 认证。
// 一件"只存在于某个人脑子里"的操作步骤，就是下一次由别人做时会做错的那一步。
//
// 地址来自后端回吐的 `publicBaseUrl`（配置 `site.publicBaseUrl` ← env
// PUBLIC_BASE_URL），**不用浏览器的 location.origin**：管理员可能经隧道或端口转发
// 访问（本地起容器就是 127.0.0.1:8080），而要发出去的是**公网**地址，不是他这次
// 用的那一个。base 为空（本地/离线姿态）时返回 null，页面据此说"本 stage 没配"。
//
// 路径形状 `<base>/git/<owner>/<repo>.git` 与 caddy 的 `handle_path /git/*` 对齐：
// handle_path 会**剥掉**该前缀再转发给 gitea，所以 gitea 侧看到的正是 `/o/r.git`。
export function repoCloneUrl(publicBaseUrl, repo) {
  const base = String(publicBaseUrl ?? '').trim().replace(/\/+$/, '');
  const name = String(repo ?? '').trim().replace(/\.git$/, '');
  if (!base || !name) return null;
  return `${base}/git/${name}.git`;
}

// 凭据用法示例。**故意把用户名与令牌写成占位符而不是真值**——这段字符串会被复制、
// 粘进聊天窗口、贴进工单，而一枚粘进工单的令牌就是一枚已泄露的令牌。
export function repoCloneHint(cloneUrl) {
  if (!cloneUrl) return null;
  return `git clone https://<USERNAME>:<TOKEN>@${cloneUrl.replace(/^https?:\/\//, '')}`;
}

// ── 分支下拉（第三百八十二批 SB-159 复审）─────────────────────────────────────────────
// GET /api/admin/repos/branches 回 {branches, total, truncated}：后端一页页向 gitea 取，撞了它自己的
// 页数上限才 truncated=true；**缺这个键 ⇒ 旧后端，截没截断未知**（不当成 false）。页面据此做两件事：
//   ① truncated=true ⇒ 一句看得见的「只列出了 n 个（共 total 个）」——此前下拉静静只装一页，
//      要找的那个分支「不在下拉里」读起来就是「没推上来」；
//   ② 缺省分支照旧优先 main／master，否则第一个。**不猜**——猜错的代价是用户在一个空分支上看半天以为
//      仓是空的。main／master 都不在清单里时**说出来**，而不是静静打开另一个分支：清单截断或旧后端时
//      它可能就在没列出的那部分里；清单完整时那就是这个仓没有，打开的是第一个。
// 返回 { branches, pick（分支名；空清单为 ''）, truncated（true／false／null＝未知）, total, notes }；
// notes 是 [{key, params, severity}]，页面逐条渲染（SB-042：判据不放 .jsx）。
export function branchPick(reply) {
  const branches = Array.isArray(reply?.branches) ? reply.branches : [];
  const truncated = typeof reply?.truncated === 'boolean' ? reply.truncated : null;
  const total = Number.isInteger(reply?.total) ? reply.total : null;
  const def = branches.find((b) => b?.name === 'main') || branches.find((b) => b?.name === 'master') || null;
  const chosen = def || branches[0] || null;
  const notes = [];
  if (truncated === true) {
    notes.push({ key: 'admin.repos.branchesTruncated',
                 params: { n: branches.length, total: total ?? '?' }, severity: 'warning' });
  }
  if (!def && chosen) {
    notes.push(truncated === false
      ? { key: 'admin.repos.noDefaultBranch', params: { name: chosen.name }, severity: 'info' }
      : { key: 'admin.repos.defaultBranchNotListed', params: { name: chosen.name }, severity: 'warning' });
  }
  return { branches, pick: chosen ? chosen.name : '', truncated, total, notes };
}
